import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Enhanced News Container
const NewsContainer = styled.div`
  padding: 140px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 100px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Featured News Section - Vicat Style
const FeaturedSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
`;

const FeaturedTitle = styled.h2`
  font-size: 3rem;
  
  color: #2c2c2c;
  margin-bottom: 60px;
  letter-spacing: -1px;
`;

const FeaturedCard = styled.div`
  position: relative;
  height: 600px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1541746972996-4e0b0f93e586?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(43, 75, 140, 0.8), rgba(59, 130, 196, 0.6));
    transition: opacity 0.4s ease;
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
  
  &:hover::after {
    opacity: 0.9;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.15);
  }
  
  &:hover .featured-content {
    transform: translateY(-20px);
  }
`;

const FeaturedContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 60px 50px;
  z-index: 2;
  transition: all 0.4s ease;
  
  .category {
    display: inline-block;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    padding: 8px 20px;
    border-radius: 25px;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 20px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  h3 {
    color: white;
    font-size: 2.5rem;
    font-weight: 400;
    line-height: 1.3;
    margin-bottom: 20px;
    letter-spacing: -1px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.2rem;
    line-height: 1.6;
    margin-bottom: 25px;
    
  }
  
  .date {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1rem;
    
  }
`;

// News Grid Section
const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 50px;
  margin-top: 60px;
  
  &.animate-in {
    .news-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .news-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .news-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .news-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
    .news-card:nth-child(5) { animation: ${scaleIn} 0.6s ease-out 0.9s both; }
    .news-card:nth-child(6) { animation: ${scaleIn} 0.6s ease-out 1.1s both; }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const NewsCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(43, 75, 140, 0.05);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
    border-color: rgba(43, 75, 140, 0.15);
  }
  
  &:hover .news-image::before {
    transform: scale(1.1);
  }
  
  &:hover .news-image::after {
    opacity: 0.8;
  }
  
  &:hover .news-content {
    transform: translateY(-5px);
  }
`;

const NewsImage = styled.div`
  height: 250px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => `url(${props.bgImage}) no-repeat center center`};
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(43, 75, 140, 0.6), rgba(59, 130, 196, 0.4));
    opacity: 0.6;
    transition: opacity 0.4s ease;
  }
  
  .category-tag {
    position: absolute;
    top: 20px;
    left: 20px;
    background: rgba(255, 255, 255, 0.9);
    color: #2B4B8C;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    z-index: 2;
    backdrop-filter: blur(10px);
  }
`;

const NewsContent = styled.div`
  padding: 40px 35px;
  transition: all 0.3s ease;
  
  .date {
    color: #6c757d;
    font-size: 0.95rem;
    margin-bottom: 15px;
    font-weight: 400;
  }
  
  h3 {
    color: #2c2c2c;
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.4;
    margin-bottom: 20px;
    letter-spacing: -0.5px;
    transition: color 0.3s ease;
  }
  
  p {
    color: #6c757d;
    line-height: 1.7;
    margin-bottom: 25px;
    font-size: 1rem;
    
  }
  
  .read-more {
    background: linear-gradient(135deg, #2B4B8C, #3B82C4);
    color: white;
    border: none;
    padding: 12px 25px;
    border-radius: 25px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    letter-spacing: 0.5px;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(43, 75, 140, 0.3);
    }
  }
`;

// Filter Section
const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 15px;
  }
`;

const FilterButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #2B4B8C, #3B82C4)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  border: 2px solid ${props => props.active ? 'transparent' : '#e9ecef'};
  padding: 12px 30px;
  border-radius: 30px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #2B4B8C, #3B82C4)' : 'rgba(43, 75, 140, 0.05)'};
    border-color: ${props => props.active ? 'transparent' : '#2B4B8C'};
    color: ${props => props.active ? 'white' : '#2B4B8C'};
    transform: translateY(-2px);
  }
`;

const News = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const newsData = [
    {
      id: 1,
      title: t('news.news1Title'),
      excerpt: t('news.news1Excerpt'),
      category: t('news.technology'),
      date: "15 Ocak 2025",
      image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 2,
      title: t('news.news2Title'),
      excerpt: t('news.news2Excerpt'),
      category: t('news.projects'),
      date: "12 Ocak 2025",
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 3,
      title: t('news.news3Title'),
      excerpt: t('news.news3Excerpt'),
      category: t('news.qualityCat'),
      date: "10 Ocak 2025",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 4,
      title: t('news.news4Title'),
      excerpt: t('news.news4Excerpt'),
      category: t('news.production'),
      date: "8 Ocak 2025",
      image: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 5,
      title: t('news.news5Title'),
      excerpt: t('news.news5Excerpt'),
      category: t('news.rnd'),
      date: "5 Ocak 2025",
      image: "https://images.unsplash.com/photo-1582719508461-905c673771fd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 6,
      title: t('news.news6Title'),
      excerpt: t('news.news6Excerpt'),
      category: t('news.success'),
      date: "3 Ocak 2025",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    }
  ];

  const categories = ['all', t('news.technology'), t('news.projects'), t('news.qualityCat'), t('news.production'), t('news.rnd'), t('news.success')];

  const filteredNews = activeFilter === 'all' 
    ? newsData 
    : newsData.filter(item => item.category === activeFilter);

  return (
    <NewsContainer>
      <Container>
        <PageTitle>{t('news.title')}</PageTitle>
        <PageSubtitle>
          {t('news.subtitle')}
        </PageSubtitle>

        <FeaturedSection 
          id="featured-section" 
          data-animate
          className={animatedSections.has('featured-section') ? 'animate-in' : ''}
        >
          <FeaturedTitle>{t('news.featured')}</FeaturedTitle>
          <FeaturedCard>
            <FeaturedContent className="featured-content">
              <span className="category">{t('news.featuredCategory')}</span>
              <h3>{t('news.featuredTitle')}</h3>
              <p>
                {t('news.featuredText')}
              </p>
              <div className="date">15 Ocak 2025</div>
            </FeaturedContent>
          </FeaturedCard>
        </FeaturedSection>

        <FilterSection>
          {categories.map(category => (
            <FilterButton
              key={category}
              active={activeFilter === category}
              onClick={() => setActiveFilter(category)}
            >
              {category === 'all' ? t('news.all') : category}
            </FilterButton>
          ))}
        </FilterSection>

        <NewsGrid 
          id="news-grid" 
          data-animate
          className={animatedSections.has('news-grid') ? 'animate-in' : ''}
        >
          {filteredNews.map((news, index) => (
            <NewsCard key={news.id} className="news-card">
              <NewsImage className="news-image" bgImage={news.image}>
                <div className="category-tag">{news.category}</div>
              </NewsImage>
              <NewsContent className="news-content">
                <div className="date">{news.date}</div>
                <h3>{news.title}</h3>
                <p>{news.excerpt}</p>
                <button className="read-more">{t('news.readMore')}</button>
              </NewsContent>
            </NewsCard>
          ))}
        </NewsGrid>
      </Container>
    </NewsContainer>
  );
};

export default News;